'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submitForm, FORMSUBMIT_EMAIL, GOOGLE_SHEETS_URL, openWhatsAppFallback } from '@/lib/formSubmit';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  projectType: string;
  propertyType: string;
  location: string;
  budget: string;
  timeline: string;
  message: string;
  preferredContact: string;
  hearAboutUs: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContactForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    projectType: '',
    propertyType: '',
    location: '',
    budget: '',
    timeline: '',
    message: '',
    preferredContact: 'email',
    hearAboutUs: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const projectTypes = [
    'Complete Home Interior',
    'Modular Kitchen',
    'Bedroom Design',
    'Living Room Design',
    'Home Office',
    'Renovation & Restoration',
    'Consultation Only'
  ];

  const propertyTypes = [
    '1 BHK Apartment',
    '2 BHK Apartment',
    '3 BHK Apartment',
    '4+ BHK Apartment',
    'Villa/Independent House',
    'Duplex/Penthouse',
    'Commercial Space'
  ];

  const budgetRanges = [
    'Under ₹5,00,000',
    '₹5,00,000 - ₹10,00,000',
    '₹10,00,000 - ₹20,00,000',
    '₹20,00,000 - ₹50,00,000',
    'Above ₹50,00,000',
    'Not Sure Yet'
  ];

  const timelines = [
    'Immediate (Within 1 month)',
    '1-3 months',
    '3-6 months',
    '6-12 months',
    'Just Planning'
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    if (!formData.projectType) {
      newErrors.projectType = 'Please select a project type';
    }

    if (!formData.propertyType) {
      newErrors.propertyType = 'Please select a property type';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.budget) {
      newErrors.budget = 'Please select a budget range';
    }

    if (!formData.timeline) {
      newErrors.timeline = 'Please select a timeline';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    // Submit to FormSubmit and Google Sheets
    const result = await submitForm(formData, FORMSUBMIT_EMAIL, GOOGLE_SHEETS_URL);

    setIsSubmitting(false);

    if (result.success) {
      setSubmitSuccess(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          projectType: '',
          propertyType: '',
          location: '',
          budget: '',
          timeline: '',
          message: '',
          preferredContact: 'email',
          hearAboutUs: ''
        });
        setSubmitSuccess(false);
      }, 3000);
    } else {
      setSubmitError(result.error || 'Failed to submit form. Please try again.');
      
      // Fallback: Open WhatsApp with the form details
      setTimeout(() => {
        openWhatsAppFallback(formData);
      }, 1500);
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8">
        <h2 className="font-headline text-2xl font-semibold text-white mb-6">Start Your Project</h2>
        <div className="space-y-4">
          <div className="h-10 bg-white/5 rounded-lg animate-pulse" />
          <div className="h-10 bg-white/5 rounded-lg animate-pulse" />
          <div className="h-10 bg-white/5 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8">
        <div className="text-center py-10">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={40} className="text-white" variant="solid" />
          </div>
          <h3 className="font-headline text-2xl font-semibold text-white mb-3">
            Thank You for Reaching Out!
          </h3>
          <p className="font-body text-white/70 max-w-md mx-auto">
            We've received your inquiry and will get back to you within 24 hours. Our design team is excited to discuss your project!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8">
      <h2 className="font-headline text-2xl font-semibold text-white mb-2">Start Your Project</h2>
      <p className="font-body text-white/70 mb-6">Fill out the form below and we'll schedule a consultation within 24 hours</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="fullName" className="block font-body text-sm font-medium text-white/90 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`w-full px-3 py-2.5 bg-white/5 border rounded-lg font-body text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.fullName ? 'border-error' : 'border-white/10'
              }`}
              placeholder="Enter your full name"
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-error font-body">{errors.fullName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block font-body text-sm font-medium text-white/90 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2.5 bg-white/5 border rounded-lg font-body text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.email ? 'border-error' : 'border-white/10'
              }`}
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error font-body">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block font-body text-sm font-medium text-white/90 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-3 py-2.5 bg-white/5 border rounded-lg font-body text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.phone ? 'border-error' : 'border-white/10'
              }`}
              placeholder="98XXXXXXXX"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-error font-body">{errors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="location" className="block font-body text-sm font-medium text-white/90 mb-2">
              Location *
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className={`w-full px-3 py-2.5 bg-white/5 border rounded-lg font-body text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.location ? 'border-error' : 'border-white/10'
              }`}
              placeholder="City, Area"
            />
            {errors.location && (
              <p className="mt-1 text-sm text-error font-body">{errors.location}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="projectType" className="block font-body text-sm font-medium text-white/90 mb-2">
              Project Type *
            </label>
            <select
              id="projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              className={`w-full px-3 py-2.5 bg-white/5 border rounded-lg font-body text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.projectType ? 'border-error' : 'border-white/10'
              }`}
            >
              <option value="" className="bg-black">Select project type</option>
              {projectTypes.map(type => (
                <option key={type} value={type} className="bg-black">{type}</option>
              ))}
            </select>
            {errors.projectType && (
              <p className="mt-1 text-sm text-error font-body">{errors.projectType}</p>
            )}
          </div>

          <div>
            <label htmlFor="propertyType" className="block font-body text-sm font-medium text-white/90 mb-2">
              Property Type *
            </label>
            <select
              id="propertyType"
              name="propertyType"
              value={formData.propertyType}
              onChange={handleChange}
              className={`w-full px-3 py-2.5 bg-white/5 border rounded-lg font-body text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.propertyType ? 'border-error' : 'border-white/10'
              }`}
            >
              <option value="" className="bg-black">Select property type</option>
              {propertyTypes.map(type => (
                <option key={type} value={type} className="bg-black">{type}</option>
              ))}
            </select>
            {errors.propertyType && (
              <p className="mt-1 text-sm text-error font-body">{errors.propertyType}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="budget" className="block font-body text-sm font-medium text-white/90 mb-2">
              Estimated Budget *
            </label>
            <select
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-white/5 border rounded-lg font-body text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.budget ? 'border-error' : 'border-white/10'
              }`}
            >
              <option value="" className="bg-black">Select budget range</option>
              {budgetRanges.map(range => (
                <option key={range} value={range} className="bg-black">{range}</option>
              ))}
            </select>
            {errors.budget && (
              <p className="mt-1 text-sm text-error font-body">{errors.budget}</p>
            )}
          </div>

          <div>
            <label htmlFor="timeline" className="block font-body text-sm font-medium text-white/90 mb-2">
              Project Timeline *
            </label>
            <select
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-white/5 border rounded-lg font-body text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
                errors.timeline ? 'border-error' : 'border-white/10'
              }`}
            >
              <option value="" className="bg-black">Select timeline</option>
              {timelines.map(time => (
                <option key={time} value={time} className="bg-black">{time}</option>
              ))}
            </select>
            {errors.timeline && (
              <p className="mt-1 text-sm text-error font-body">{errors.timeline}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block font-body text-sm font-medium text-white/90 mb-2">
            Tell Us About Your Vision
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg font-body text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all resize-none"
            placeholder="Share your design preferences, inspirations, or any specific requirements..."
          />
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-white/90 mb-3">
            Preferred Contact Method
          </label>
          <div className="flex flex-wrap gap-4">
            {['email', 'phone', 'whatsapp'].map(method => (
              <label key={method} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="preferredContact"
                  value={method}
                  checked={formData.preferredContact === method}
                  onChange={handleChange}
                  className="w-4 h-4 text-white border-white/20 focus:ring-2 focus:ring-white/30"
                />
                <span className="ml-2 font-body text-sm text-white/90 capitalize">{method}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="hearAboutUs" className="block font-body text-sm font-medium text-white/90 mb-2">
            How did you hear about us?
          </label>
          <select
            id="hearAboutUs"
            name="hearAboutUs"
            value={formData.hearAboutUs}
            onChange={handleChange}
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg font-body text-white focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
          >
            <option value="" className="bg-black">Select an option</option>
            <option value="google" className="bg-black">Google Search</option>
            <option value="instagram" className="bg-black">Instagram</option>
            <option value="facebook" className="bg-black">Facebook</option>
             <option value="linkedin" className="bg-black">LinkedIn</option>
            <option value="referral" className="bg-black">Friend/Family Referral</option>
            <option value="magazine" className="bg-black">Magazine/Publication</option>
           
            <option value="other" className="bg-black">Other</option>
          </select>
        </div>

        {submitError && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
            <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0 mt-0.5" variant="solid" />
            <div>
              <p className="font-body text-sm text-error font-semibold mb-1">Submission Failed</p>
              <p className="font-body text-sm text-error/80">{submitError}</p>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-white text-black font-body font-semibold py-3 rounded-full hover:scale-105 transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <span>Schedule Consultation</span>
              <Icon name="ArrowRightIcon" size={20} />
            </>
          )}
        </button>

        <p className="text-xs text-white/40 font-body text-center">
          By submitting this form, you agree to our privacy policy and terms of service. We'll never share your information with third parties.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;